import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { maskCPF, maskPhone } from "../lib/format";
import { consumeRouteHint, returnHomeIfRequested } from "../lib/setupRoute";
import { AppointmentEditor } from "./Agenda";

/**
 * Clients — Cadastro de Clientes do Salão.
 * Mesmo padrão visual do módulo Colaboradores:
 *   - welcome bar compacto (busca + Novo Cliente + avatar)
 *   - 3 pílulas com contornos dourados sutis
 *     1) FINANCEIRO DO CLIENTE
 *     2) CADASTRO DO CLIENTE (com gráfico de gastos)
 *     3) PRÓXIMOS AGENDAMENTOS
 */
const STATUS_OPTIONS = [
    { id: "VIP", label: "VIP", color: "violet", border: "border-violet-500/50", bg: "bg-violet-500/15", text: "text-violet-200" },
    { id: "Em Dia", label: "Em Dia", color: "emerald", border: "border-emerald-500/50", bg: "bg-emerald-500/15", text: "text-emerald-200" },
    { id: "Atrasado", label: "Atrasado", color: "red", border: "border-red-500/50", bg: "bg-red-500/15", text: "text-red-200" },
];

const blankClient = () => ({
    id: null,
    name: "",
    document: "",
    phone: "",
    email: "",
    gender: "",
    birthDate: "",
    address: "",
    notes: "",
    preferences: [],
    allergies: "",
    paymentStatus: "Em Dia",
    totalSpent: 0,
    balance: 0,
    transactions: [],
});

const Clients = () => {
    const { clients, upsertClient, deleteClient, toggleClientActive, setActiveModule } = useSalon();
    const [query, setQuery] = useState("");
    // No auto-selection: user must explicitly pick a client via search
    const [activeClientId, setActiveClientId] = useState(null);
    // Views: 'hub' | 'search' | 'registry' | 'financial' | 'schedule' | 'marketing'
    const [tab, setTab] = useState("hub");
    const [creating, setCreating] = useState(false);
    const [bookingState, setBookingState] = useState(null);
    // Delete-confirmation modal
    const [confirmDelete, setConfirmDelete] = useState(null); // {id, name} | null

    // Recovery only when active client was actually deleted (avoid re-selecting on mount).
    useEffect(() => {
        if (!creating && activeClientId && !clients.find((c) => c.id === activeClientId)) {
            setActiveClientId(null);
        }
    }, [clients, activeClientId, creating]);

    // Deep link from the Home setup checklist: open the new-client form directly.
    useEffect(() => {
        if (consumeRouteHint("kinng.route.clients.view") === "new-client") {
            setCreating(true);
            setTab("registry");
        }
    }, []);

    // After creating (or backing out of) the deep-linked form, return to Home if asked.
    const finishCreate = () => {
        setCreating(false);
        if (!returnHomeIfRequested(() => setActiveModule("home"))) setTab("hub");
    };

    const filteredClients = useMemo(() => {
        const raw = query.trim();
        if (!raw) return clients;
        const digitsQuery = raw.replace(/\D/g, "");
        const lowerQuery = raw.toLowerCase();
        return clients.filter((c) => {
            const phoneDigits = (c.phone || "").replace(/\D/g, "");
            const email = (c.email || "").toLowerCase();
            const name = (c.name || "").toLowerCase();
            if (digitsQuery.length >= 2 && phoneDigits.includes(digitsQuery)) return true;
            if (name.includes(lowerQuery)) return true;
            if (email.includes(lowerQuery)) return true;
            return false;
        });
    }, [clients, query]);

    // activeClient can be null when nothing was picked yet.
    const activeClient = creating
        ? blankClient()
        : (activeClientId ? clients.find((c) => c.id === activeClientId) : null);

    const selectClient = (id) => {
        setCreating(false);
        setActiveClientId(id);
        setQuery("");
    };

    // ─── HUB CARDS ────────────────────────────────────────────────────────
    // 4 cards: Cadastro (busca) / Novo Cliente / Agendar / Marketing.
    // "Financeiro & Comportamento" continua acessível como aba interna dentro da ficha.
    const HUB_CARDS = [
        {
            id: "search",
            label: "Cadastro de Clientes",
            icon: "fa-magnifying-glass",
            subtitle: "Buscar por nome, telefone ou e-mail",
            accent: "from-amber-500/30 to-amber-400/5",
        },
        {
            id: "new",
            label: "Cadastrar Novo Cliente",
            icon: "fa-user-plus",
            subtitle: "Ficha em branco · comece do zero",
            accent: "from-emerald-500/25 to-amber-500/5",
        },
        {
            id: "book",
            label: "Agendar um Cliente",
            icon: "fa-calendar-plus",
            subtitle: "Novo agendamento na Agenda",
            accent: "from-sky-500/25 to-amber-500/5",
        },
        {
            id: "marketing",
            label: "Marketing do Cliente",
            icon: "fa-bullhorn",
            subtitle: "Cupons, campanhas e reativação",
            accent: "from-violet-500/25 to-amber-500/5",
        },
    ];

    // Internal tab pills shown INSIDE the client detail view
    const DETAIL_TABS = [
        { id: "registry", label: "Cadastro do Cliente", icon: "fa-id-badge" },
        { id: "financial", label: "Financeiro & Comportamento", icon: "fa-chart-mixed" },
        { id: "schedule", label: "Próximos Agendamentos", icon: "fa-calendar-day" },
        { id: "marketing", label: "Marketing do Cliente", icon: "fa-bullhorn" },
    ];

    const openHubCard = (cardId) => {
        if (cardId === "search") {
            setCreating(false);
            setTab("search");
            return;
        }
        if (cardId === "new") {
            setCreating(true);
            setTab("registry");
            return;
        }
        if (cardId === "book") {
            // Open the same appointment editor used by Agenda, pre-populated with active client if any.
            setBookingState({
                mode: "new",
                prefill: activeClient
                    ? { clientId: activeClient.id, clientName: activeClient.name }
                    : {},
            });
            return;
        }
        if (cardId === "marketing") {
            if (!activeClient) {
                toast.info("Selecione um cliente primeiro", {
                    description: "Use a busca em 'Cadastro de Clientes' para escolher.",
                });
                setTab("search");
                return;
            }
            setTab("marketing");
        }
    };

    const isHub = tab === "hub" && !creating;
    const isSearchView = tab === "search" && !creating;
    const isDetailView = ["registry", "financial", "schedule", "marketing"].includes(tab);

    return (
        <div data-testid="clients-module" className="space-y-5">
            {/* Welcome bar — only visible on hub-adjacent views (not on internal detail). */}
            {(isHub || isSearchView) && (
                <header
                    data-testid="clients-welcome-bar"
                    className="rounded-xl px-4 py-2.5 flex items-center gap-2 border border-amber-500/15 bg-black/40"
                >
                    {isSearchView && (
                        <button
                            data-testid="clients-back-to-hub"
                            onClick={() => { setCreating(false); setTab("hub"); setQuery(""); }}
                            className="min-h-[40px] px-3 rounded-lg border border-amber-500/25 hover:border-amber-400/60 hover:bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider font-bold transition-all flex items-center gap-2"
                            title="Voltar aos cards"
                        >
                            <i className="fa-solid fa-arrow-left"></i>
                            <span className="hidden sm:inline">Voltar</span>
                        </button>
                    )}
                    <div className="flex-1" />

                    <button
                        data-testid="clients-new-btn"
                        onClick={() => {
                            setCreating(true);
                            setTab("registry");
                        }}
                        className="min-h-[40px] px-3 rounded-lg border border-amber-500/35 hover:border-amber-400/70 hover:bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider font-bold transition-all flex items-center gap-2"
                        title="Novo cliente"
                    >
                        <i className="fa-solid fa-plus"></i>
                        <span className="hidden sm:inline">Novo Cliente</span>
                    </button>

                    {activeClient && !creating && (
                        <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-amber-500/10 border border-amber-500/30 text-amber-300 font-bold text-sm ring-1 ring-amber-500/20">
                            {(activeClient.name || "?").charAt(0).toUpperCase()}
                        </div>
                    )}
                </header>
            )}

            {/* Detail-view header (compact back-to-hub bar) */}
            {isDetailView && (
                <header
                    data-testid="clients-detail-bar"
                    className="rounded-xl px-4 py-2.5 flex items-center gap-2 border border-amber-500/15 bg-black/40"
                >
                    <button
                        data-testid="clients-back-to-hub"
                        onClick={() => { setCreating(false); setTab("hub"); }}
                        className="min-h-[40px] px-3 rounded-lg border border-amber-500/25 hover:border-amber-400/60 hover:bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider font-bold transition-all flex items-center gap-2"
                        title="Voltar aos cards"
                    >
                        <i className="fa-solid fa-arrow-left"></i>
                        <span className="hidden sm:inline">Voltar</span>
                    </button>
                    <div className="flex-1" />
                    {activeClient && !creating && (
                        <div className="flex items-center gap-2.5">
                            <div className="text-right hidden sm:block">
                                <div className="text-[9px] uppercase tracking-[0.2em] text-amber-300/60 font-bold">Cliente</div>
                                <div className="text-xs text-amber-100 font-bold">{activeClient.name}</div>
                            </div>
                            <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-amber-500/10 border border-amber-500/30 text-amber-300 font-bold text-sm ring-1 ring-amber-500/20">
                                {(activeClient.name || "?").charAt(0).toUpperCase()}
                            </div>
                        </div>
                    )}
                </header>
            )}

            {/* HUB — static cards (tap to open) */}
            {isHub && (
                <>
                    <div className="flex items-center justify-between gap-3 px-1">
                        <div>
                            <h2 className="salon-heading text-xl text-amber-100">Central de Clientes</h2>
                            <p className="text-[11px] uppercase tracking-[0.2em] text-amber-300/60 mt-0.5">
                                Escolha uma área
                            </p>
                        </div>
                        {activeClient && (
                            <div className="text-right hidden sm:block">
                                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 font-bold">Cliente ativo</div>
                                <div className="text-sm text-amber-100 font-bold mt-0.5">{activeClient.name}</div>
                            </div>
                        )}
                    </div>

                    <div
                        data-testid="clients-hub-cards"
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-fr"
                    >
                        {HUB_CARDS.map((c) => (
                            <HubCard
                                key={c.id}
                                card={c}
                                onOpen={() => openHubCard(c.id)}
                            />
                        ))}
                    </div>
                </>
            )}

            {/* SEARCH-ONLY view (card: Cadastro de Clientes) */}
            {isSearchView && (
                <SearchClientView
                    query={query}
                    setQuery={setQuery}
                    allClients={clients}
                    filteredClients={filteredClients}
                    totalClients={clients.length}
                    onPick={(id) => { selectClient(id); setTab("registry"); }}
                    onNew={() => { setCreating(true); setTab("registry"); }}
                    onToggleActive={(c) => toggleClientActive(c.id)}
                    onAskDelete={(c) => setConfirmDelete({ id: c.id, name: c.name })}
                />
            )}

            {/* Internal tab pills — visible only on the client detail view */}
            {isDetailView && !creating && (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {DETAIL_TABS.map((t) => {
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                data-testid={`clients-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`gold-pill ${active ? "gold-pill-active" : ""} min-h-[60px] rounded-2xl px-4 flex items-center justify-center gap-3 text-sm md:text-base font-bold uppercase tracking-[0.15em]`}
                            >
                                <i className={`fa-solid ${t.icon}`}></i>
                                <span className="truncate">{t.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {tab === "financial" && activeClient && !creating && <ClientFinancialView client={activeClient} />}
            {tab === "registry" && activeClient && (
                <ClientRegistryView
                    key={creating ? "new" : activeClient.id}
                    client={activeClient}
                    isNew={creating}
                    onDoneCreate={finishCreate}
                    onCancelCreate={finishCreate}
                />
            )}
            {tab === "schedule" && activeClient && !creating && <ClientScheduleView client={activeClient} />}
            {tab === "marketing" && activeClient && !creating && <ClientMarketingView client={activeClient} />}

            {/* Appointment editor overlay — used by "Agendar um Cliente" card */}
            {bookingState && (
                <AppointmentEditor
                    state={bookingState}
                    onClose={() => setBookingState(null)}
                    defaultDate={new Date()}
                />
            )}

            {/* Delete confirmation modal */}
            {confirmDelete && (
                <ConfirmDeleteClientModal
                    name={confirmDelete.name}
                    onCancel={() => setConfirmDelete(null)}
                    onConfirm={() => {
                        deleteClient(confirmDelete.id);
                        if (activeClientId === confirmDelete.id) setActiveClientId(null);
                        toast.success("Cliente excluído", { description: `${confirmDelete.name} foi removido permanentemente.` });
                        setConfirmDelete(null);
                    }}
                />
            )}
        </div>
    );
};

// ─── Search-only View ─────────────────────────────────────────────────────
const ALPHABET = ["A","B","C","D","E","F","G","H","I","J","K","L","M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z","#"];

const normalizeLetter = (name) => {
    const first = (name || "?").trim().charAt(0).toUpperCase();
    // Normalize accented letters to base (e.g. Á -> A)
    const base = first.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (/^[A-Z]$/.test(base)) return base;
    return "#";
};

const SearchClientView = ({ query, setQuery, allClients, filteredClients, totalClients, onPick, onNew, onToggleActive, onAskDelete }) => {
    // Which list to render: filtered when searching, full when not
    const listSource = query.trim() ? filteredClients : allClients;

    // Group + sort alphabetically
    const groups = useMemo(() => {
        const map = {};
        listSource.forEach((c) => {
            const letter = normalizeLetter(c.name);
            if (!map[letter]) map[letter] = [];
            map[letter].push(c);
        });
        Object.keys(map).forEach((k) => {
            map[k].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" }));
        });
        // Return alphabet order (letters that exist)
        return ALPHABET.filter((l) => map[l] && map[l].length > 0).map((l) => ({ letter: l, items: map[l] }));
    }, [listSource]);

    const availableLetters = useMemo(() => new Set(groups.map((g) => g.letter)), [groups]);

    const scrollToLetter = (letter) => {
        const el = document.getElementById(`clients-letter-${letter}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <section data-testid="clients-search-view" className="space-y-5">
            <div className="text-center py-2">
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl border border-amber-500/40 bg-amber-500/10 text-amber-300 text-xl mb-2">
                    <i className="fa-solid fa-magnifying-glass"></i>
                </div>
                <h2 className="salon-heading text-2xl lg:text-3xl text-amber-100">Buscar Cliente</h2>
                <p className="text-[11px] uppercase tracking-[0.2em] text-amber-300/60 mt-1">
                    {totalClients} clientes cadastrados
                </p>
            </div>

            <div className="max-w-3xl mx-auto">
                <div className="relative">
                    <i className="fa-solid fa-magnifying-glass absolute left-4 top-1/2 -translate-y-1/2 text-amber-300/60"></i>
                    <input
                        data-testid="clients-search-input"
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Nome, telefone ou e-mail…"
                        className="w-full min-h-[52px] rounded-2xl bg-black/60 border border-amber-500/25 pl-12 pr-4 text-base text-amber-100 outline-none focus:border-amber-400/70 transition-colors placeholder:text-amber-200/30"
                    />
                </div>
            </div>

            {/* Alphabet index */}
            <div
                data-testid="clients-alpha-index"
                className="max-w-3xl mx-auto flex flex-wrap gap-1 justify-center px-2 py-2 rounded-xl bg-black/30 border border-amber-500/10"
            >
                {ALPHABET.map((l) => {
                    const has = availableLetters.has(l);
                    return (
                        <button
                            key={l}
                            data-testid={`clients-alpha-${l}`}
                            onClick={() => has && scrollToLetter(l)}
                            disabled={!has}
                            className={`h-7 w-7 text-[11px] font-bold rounded-md transition-all ${
                                has
                                    ? "text-amber-200 hover:bg-amber-500/15 hover:text-amber-100 cursor-pointer"
                                    : "text-amber-200/20 cursor-default"
                            }`}
                        >
                            {l}
                        </button>
                    );
                })}
            </div>

            {/* Grouped list */}
            <div className="max-w-3xl mx-auto">
                {groups.length === 0 ? (
                    query.trim() === "" ? (
                        <div data-testid="clients-search-empty-query" className="text-center py-10">
                            <p className="text-amber-200/40 text-sm">Nenhum cliente cadastrado ainda.</p>
                        </div>
                    ) : (
                        <div data-testid="clients-search-no-results" className="rounded-2xl border border-amber-500/20 bg-black/40 p-8 text-center">
                            <i className="fa-solid fa-user-slash text-3xl text-amber-300/40 mb-3"></i>
                            <p className="text-amber-100 text-sm font-bold">Nenhum cliente encontrado</p>
                            <p className="text-amber-200/50 text-xs mt-1">Verifique a grafia ou cadastre um novo cliente.</p>
                            <button
                                data-testid="clients-search-cta-new"
                                onClick={onNew}
                                className="mt-4 min-h-[44px] px-4 rounded-xl border border-amber-500/40 hover:border-amber-400/70 hover:bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider font-bold transition-all inline-flex items-center gap-2"
                            >
                                <i className="fa-solid fa-user-plus"></i>
                                Cadastrar novo cliente
                            </button>
                        </div>
                    )
                ) : (
                    <div data-testid="clients-alpha-list" className="space-y-4">
                        {groups.map((g) => (
                            <div key={g.letter} id={`clients-letter-${g.letter}`} data-testid={`clients-group-${g.letter}`}>
                                <div className="sticky top-0 z-10 px-3 py-1.5 rounded-lg bg-slate-950/85 backdrop-blur border-l-2 border-amber-400 mb-2 shadow-[0_2px_10px_-2px_rgba(0,0,0,0.6)]">
                                    <span className="salon-heading text-lg text-amber-300">{g.letter}</span>
                                    <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-200/40 font-bold">
                                        {g.items.length} {g.items.length === 1 ? "cliente" : "clientes"}
                                    </span>
                                </div>
                                <ul className="space-y-1.5">
                                    {g.items.map((c) => (
                                        <ClientRow
                                            key={c.id}
                                            client={c}
                                            onPick={() => onPick(c.id)}
                                            onToggleActive={() => onToggleActive(c)}
                                            onAskDelete={() => onAskDelete(c)}
                                        />
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

// ─── Client Row (name + toggle + trash) ───────────────────────────────────
const ClientRow = ({ client, onPick, onToggleActive, onAskDelete }) => {
    const isInactive = client.active === false;
    const { brand } = useSalon();
    const sendBookingLink = (e) => {
        e.stopPropagation();
        const shopSlug = (brand.shopId || "salao").toLowerCase();
        const shopName = brand.shopName || "seu salão";
        const link = `https://kinng.app/${shopSlug}/booking?cliente=${client.id}`;
        const msg = `Olá ${(client.name || "").split(" ")[0]}! Agende sua visita ao ${shopName} pelo link: ${link}`;
        const phone = (client.phone || "").replace(/\D/g, "");
        const url = phone
            ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
            : `https://wa.me/?text=${encodeURIComponent(msg)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };
    return (
        <li
            data-testid={`clients-search-result-${client.id}`}
            data-inactive={isInactive ? "true" : "false"}
            className={`flex items-center gap-3 rounded-xl border transition-all p-3 pr-2 ${
                isInactive
                    ? "border-white/5 bg-black/30 opacity-60"
                    : "border-amber-500/15 bg-black/40 hover:border-amber-400/50 hover:bg-amber-500/[0.05]"
            }`}
        >
            <button
                onClick={onPick}
                className="flex-1 flex items-center gap-3 min-w-0 text-left"
                title="Abrir ficha do cliente"
            >
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center border font-bold text-sm shrink-0 ${
                    isInactive
                        ? "bg-slate-800/40 border-white/10 text-slate-500"
                        : "bg-amber-500/12 border-amber-500/35 text-amber-200"
                }`}>
                    {(client.name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-sm font-bold truncate ${isInactive ? "text-slate-400 line-through decoration-slate-600" : "text-amber-100"}`}>
                        {client.name}
                    </div>
                    <div className="flex items-center gap-3 text-[10px] font-mono mt-0.5 text-amber-200/50">
                        {client.phone && <span><i className="fa-solid fa-phone mr-1 opacity-60"></i>{client.phone}</span>}
                        {client.email && <span className="truncate hidden sm:inline"><i className="fa-solid fa-envelope mr-1 opacity-60"></i>{client.email}</span>}
                    </div>
                </div>
            </button>

            {/* Send booking link (WhatsApp) */}
            <button
                data-testid={`clients-send-link-${client.id}`}
                onClick={sendBookingLink}
                title="Enviar link de agendamento via WhatsApp"
                className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-emerald-300/70 hover:text-emerald-200 hover:bg-emerald-500/10 transition-all"
            >
                <i className="fa-solid fa-calendar-plus text-sm"></i>
            </button>

            {/* Toggle switch */}
            <button
                data-testid={`clients-toggle-active-${client.id}`}
                onClick={onToggleActive}
                role="switch"
                aria-checked={!isInactive}
                title={isInactive ? "Reativar cliente" : "Desativar cliente"}
                className={`relative shrink-0 h-6 w-11 rounded-full border transition-all ${
                    isInactive
                        ? "bg-slate-700/60 border-slate-600"
                        : "bg-emerald-500/30 border-emerald-400/60"
                }`}
            >
                <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full transition-all shadow-md ${
                        isInactive ? "left-0.5 bg-slate-400" : "left-[24px] bg-emerald-300"
                    }`}
                ></span>
            </button>

            {/* Trash icon */}
            <button
                data-testid={`clients-delete-${client.id}`}
                onClick={onAskDelete}
                title="Excluir cliente"
                className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all"
            >
                <i className="fa-solid fa-trash-can text-sm"></i>
            </button>
        </li>
    );
};

// ─── Confirm Delete Modal ─────────────────────────────────────────────────
const ConfirmDeleteClientModal = ({ name, onCancel, onConfirm }) => (
    <div
        data-testid="clients-delete-confirm-modal"
        className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onCancel}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-red-500/30 bg-slate-950 shadow-[0_30px_80px_-10px_rgba(239,68,68,0.35)] p-6"
        >
            <div className="flex items-start gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center bg-red-500/15 border border-red-500/40 text-red-300 text-lg shrink-0">
                    <i className="fa-solid fa-triangle-exclamation"></i>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="salon-heading text-xl text-white">Excluir Cliente</h3>
                    <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                        Tem certeza que deseja excluir <span className="font-bold text-white">{name}</span>? Esta ação não pode ser desfeita.
                    </p>
                </div>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
                <button
                    data-testid="clients-delete-cancel"
                    onClick={onCancel}
                    className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-white/30 hover:bg-white/5 text-slate-200 text-xs uppercase tracking-wider font-bold transition-all"
                >
                    Cancelar
                </button>
                <button
                    data-testid="clients-delete-confirm"
                    onClick={onConfirm}
                    className="min-h-[44px] px-4 rounded-xl bg-red-500 hover:bg-red-400 text-white text-xs uppercase tracking-wider font-bold transition-all flex items-center gap-2"
                >
                    <i className="fa-solid fa-trash-can"></i>
                    Excluir
                </button>
            </div>
        </div>
    </div>
);

// ─── Hub Card (static · tap to open) ──────────────────────────────────────
const HubCard = ({ card, onOpen }) => {
    return (
        <div
            data-testid={`clients-hub-card-${card.id}`}
            onClick={onOpen}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen();
                }
            }}
            role="button"
            tabIndex={0}
            className="group relative rounded-2xl border border-amber-500/20 bg-black/50 p-5 text-left overflow-hidden transition-all duration-300 cursor-pointer hover:border-amber-400/55 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(212,175,55,0.45)] active:translate-y-0 active:scale-[0.99]"
        >
            {/* Background gradient */}
            <div className={`absolute inset-0 bg-gradient-to-br ${card.accent} opacity-60 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}></div>

            <div className="relative h-full flex flex-col min-h-[170px]">
                {/* Icon */}
                <div className="h-14 w-14 rounded-2xl flex items-center justify-center border border-amber-400/30 bg-gradient-to-br from-amber-500/25 to-amber-700/5 text-amber-300 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3">
                    <i className={`fa-solid ${card.icon} text-xl`}></i>
                </div>

                {/* Title + subtitle */}
                <div className="mt-auto">
                    <h3 className="salon-heading text-lg lg:text-xl text-amber-100 leading-tight">
                        {card.label}
                    </h3>
                    <p className="text-[11px] text-amber-200/55 mt-1 leading-snug">
                        {card.subtitle}
                    </p>
                </div>

                {/* Arrow */}
                <div className="absolute bottom-0 right-0 text-amber-300/40 group-hover:text-amber-200 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all">
                    <i className="fa-solid fa-arrow-up-right"></i>
                </div>
            </div>

            {/* Bottom hover accent */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
        </div>
    );
};

// ─── VIEW: Financeiro & Comportamento ─────────────────────────────────────
const PERIODS_SIMPLE = {
    weekly: { days: 7, label: "Semanal" },
    biweekly: { days: 15, label: "Quinzenal" },
    monthly: { days: 30, label: "Mensal" },
    quarterly: { days: 90, label: "Trimestral" },
};

const WEEKDAY_NAMES = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];

const ClientFinancialView = ({ client }) => {
    const { setActiveModule, setPendingWizardSeed } = useSalon();
    const [periodId, setPeriodId] = useState("monthly");
    const [chartPeriodId, setChartPeriodId] = useState("monthly");

    // Lifetime behavioral stats (independent of period filter)
    const behavior = useMemo(() => {
        const txns = [...(client.transactions || [])].sort((a, b) => a.date - b.date);
        const total = txns.length;
        const lifetimeSpent = txns.reduce((s, t) => s + (t.value || 0), 0);
        const avgTicket = total > 0 ? lifetimeSpent / total : 0;

        // Frequency: avg days between visits
        let avgInterval = null;
        if (total >= 2) {
            const intervals = [];
            for (let i = 1; i < txns.length; i++) {
                intervals.push((txns[i].date - txns[i - 1].date) / 86400000);
            }
            avgInterval = intervals.reduce((s, n) => s + n, 0) / intervals.length;
        }

        // Preferred weekday
        const weekdayCount = [0, 0, 0, 0, 0, 0, 0];
        txns.forEach((t) => {
            const d = new Date(t.date).getDay();
            weekdayCount[d]++;
        });
        let preferredWeekday = null;
        let preferredWeekdayCount = 0;
        weekdayCount.forEach((c, i) => {
            if (c > preferredWeekdayCount) {
                preferredWeekdayCount = c;
                preferredWeekday = i;
            }
        });

        // Top services
        const serviceMap = {};
        txns.forEach((t) => {
            const k = t.service || "Outros";
            if (!serviceMap[k]) serviceMap[k] = { name: k, count: 0, total: 0 };
            serviceMap[k].count++;
            serviceMap[k].total += t.value || 0;
        });
        const topServices = Object.values(serviceMap).sort((a, b) => b.count - a.count);

        // Days since last visit
        const lastVisit = txns.length > 0 ? txns[txns.length - 1].date : null;
        const daysSinceLast = lastVisit ? Math.floor((Date.now() - lastVisit) / 86400000) : null;

        return { total, lifetimeSpent, avgTicket, avgInterval, preferredWeekday, preferredWeekdayCount, topServices, daysSinceLast };
    }, [client]);

    // Period-filtered transactions for the table
    const transactions = useMemo(() => {
        const cutoff = Date.now() - PERIODS_SIMPLE[periodId].days * 86400000;
        return (client.transactions || []).filter((t) => t.date >= cutoff).sort((a, b) => b.date - a.date);
    }, [client, periodId]);

    // Chart data (6 periods)
    const chartPeriod = CHART_PERIODS.find((p) => p.id === chartPeriodId);
    const chartData = useMemo(() => {
        if (!chartPeriod) return { buckets: [], maxValue: 0 };
        const now = Date.now();
        const startTime = now - chartPeriod.days * 86400000;
        const txns = (client.transactions || []).filter((t) => t.date >= startTime);
        const bucketSize = chartPeriod.days / chartPeriod.buckets;
        const buckets = Array.from({ length: chartPeriod.buckets }, (_, i) => {
            const bucketStart = startTime + i * bucketSize * 86400000;
            const bucketEnd = bucketStart + bucketSize * 86400000;
            const items = txns.filter((t) => t.date >= bucketStart && t.date < bucketEnd);
            const total = items.reduce((s, t) => s + (t.value || 0), 0);
            const label = formatBucketLabel(new Date(bucketStart), chartPeriod.unit);
            return { label, total, count: items.length };
        });
        const maxValue = Math.max(1, ...buckets.map((b) => b.total));
        return { buckets, maxValue };
    }, [client, chartPeriod]);

    // Marketing suggestions (rule-based)
    const suggestions = useMemo(() => {
        const list = [];
        const clientSeed = { clientId: client.id, clientName: client.name };
        // Loyal client losing frequency
        if (behavior.avgInterval && behavior.daysSinceLast && behavior.daysSinceLast > behavior.avgInterval * 1.5) {
            list.push({
                kind: "reactivate",
                icon: "fa-user-clock",
                title: "Reativação urgente",
                detail: `Cliente costuma vir a cada ${Math.round(behavior.avgInterval)} dias, mas faz ${behavior.daysSinceLast} dias que não aparece. Envie uma mensagem agora.`,
                cta: "Reativar via Marketing",
                wizardSeed: {
                    ...clientSeed,
                    audienceId: behavior.avgInterval >= 40 ? "inactive45" : "inactive30",
                    templateId: "tpl-reativacao-corte",
                },
            });
        }
        // VIP / Top service upsell
        if (behavior.topServices.length > 0 && behavior.total >= 3) {
            const top = behavior.topServices[0];
            list.push({
                kind: "upsell",
                icon: "fa-arrow-up-right-dots",
                title: `Cliente fiel de ${top.name}`,
                detail: `${top.count} atendimentos do serviço favorito. Sugira um combo (ex: hidratação ou produto de manutenção).`,
                cta: "Criar campanha de up-sell",
                wizardSeed: {
                    ...clientSeed,
                    audienceId: "post-service",
                    templateId: "tpl-upsell",
                },
            });
        }
        // High ticket
        if (behavior.avgTicket > 100) {
            list.push({
                kind: "vip",
                icon: "fa-crown",
                title: "Perfil premium",
                detail: `Ticket médio R$ ${behavior.avgTicket.toFixed(2)}. Vale oferecer plano de fidelidade VIP ou serviços exclusivos.`,
                cta: "Criar campanha de indicação",
                wizardSeed: {
                    ...clientSeed,
                    audienceId: "vip-referral",
                    templateId: "tpl-indicacao",
                },
            });
        }
        // Birthday
        if (client.birthDate) {
            const [, m, d] = client.birthDate.split("-").map(Number);
            const now = new Date();
            const bday = new Date(now.getFullYear(), m - 1, d);
            const diffDays = Math.floor((bday - now) / 86400000);
            if (diffDays >= 0 && diffDays <= 30) {
                list.push({
                    kind: "birthday",
                    icon: "fa-cake-candles",
                    title: "Aniversário próximo",
                    detail: `Faz aniversário em ${diffDays === 0 ? "hoje" : `${diffDays} dia(s)`}. Envie um cupom personalizado.`,
                    cta: "Enviar parabéns + cupom",
                    wizardSeed: {
                        ...clientSeed,
                        audienceId: "birthday-month",
                        templateId: "tpl-aniversario",
                    },
                });
            }
        }
        // No suggestions: positive vibe
        if (list.length === 0) {
            list.push({
                kind: "all-good",
                icon: "fa-thumbs-up",
                title: "Cliente em dia",
                detail: "Não há ações urgentes. Continue cuidando bem para manter a frequência saudável.",
                cta: null,
            });
        }
        return list;
    }, [behavior, client]);

    const handleSuggestionAction = (suggestion) => {
        if (!suggestion.wizardSeed) return;
        setPendingWizardSeed(suggestion.wizardSeed);
        setActiveModule("marketing");
        toast.success("Wizard pré-preenchido", {
            description: `Marketing → ${suggestion.title}`,
        });
    };

    const statusStyle = STATUS_OPTIONS.find((s) => s.id === client.paymentStatus) || STATUS_OPTIONS[1];
    const topServiceMax = Math.max(1, ...behavior.topServices.map((s) => s.count));

    // Power-BI sparkline series for each KPI
    const txns6mo = useMemo(() => (client.transactions || []), [client]);
    const monthlyTickets = useMemo(() => {
        const buckets = Array(6).fill(0);
        const counts = Array(6).fill(0);
        const now = new Date();
        txns6mo.forEach((t) => {
            const d = new Date(t.date);
            const monthsAgo = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
            if (monthsAgo >= 0 && monthsAgo < 6) {
                buckets[5 - monthsAgo] += t.value || 0;
                counts[5 - monthsAgo] += 1;
            }
        });
        const avgs = buckets.map((sum, i) => (counts[i] > 0 ? sum / counts[i] : 0));
        return { sums: buckets, avgs, counts };
    }, [txns6mo]);
    const weekdayDist = useMemo(() => {
        const w = [0, 0, 0, 0, 0, 0, 0];
        txns6mo.forEach((t) => { w[new Date(t.date).getDay()]++; });
        return w;
    }, [txns6mo]);
    const cumulativeLifetime = useMemo(() => {
        const out = [];
        let acc = 0;
        for (const s of monthlyTickets.sums) {
            acc += s;
            out.push(acc);
        }
        return out;
    }, [monthlyTickets]);

    return (
        <div data-testid="clients-financial-view" className="space-y-4">
            {/* HERO: Behavioral KPIs (Power-BI style with sparklines) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="clients-behavioral-kpis">
                <KpiWithSpark
                    icon="fa-receipt"
                    label="Ticket Médio"
                    value={`R$ ${behavior.avgTicket.toFixed(2)}`}
                    sub="6 meses · evolução"
                    accent="amber"
                    series={monthlyTickets.avgs}
                    testId="kpi-avg-ticket"
                />
                <KpiWithSpark
                    icon="fa-clock-rotate-left"
                    label="Frequência de visita"
                    value={behavior.avgInterval ? `${Math.round(behavior.avgInterval)} dias` : "—"}
                    sub="visitas / mês"
                    accent="emerald"
                    series={monthlyTickets.counts}
                    testId="kpi-frequency"
                />
                <KpiWithSpark
                    icon="fa-calendar-check"
                    label="Dia mais visitado"
                    value={behavior.preferredWeekday !== null ? WEEKDAY_NAMES[behavior.preferredWeekday] : "—"}
                    sub="distribuição semanal"
                    accent="amber"
                    series={[weekdayDist[1], weekdayDist[2], weekdayDist[3], weekdayDist[4], weekdayDist[5], weekdayDist[6]]}
                    testId="kpi-preferred-day"
                />
            </div>

            {/* Secondary stats — Status + lifetime */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiWithSpark
                    icon="fa-sack-dollar"
                    label="Total na vida"
                    value={`R$ ${behavior.lifetimeSpent.toFixed(2)}`}
                    accent="emerald"
                    series={cumulativeLifetime}
                    testId="kpi-lifetime"
                />
                <KpiWithSpark
                    icon="fa-scissors"
                    label="Atendimentos"
                    value={behavior.total}
                    accent="amber"
                    series={monthlyTickets.counts}
                    testId="kpi-visits"
                />
                <KpiWithSpark
                    icon="fa-history"
                    label="Última visita"
                    value={behavior.daysSinceLast !== null ? `${behavior.daysSinceLast}d atrás` : "—"}
                    sub={behavior.daysSinceLast !== null && behavior.avgInterval ? (behavior.daysSinceLast > behavior.avgInterval * 1.3 ? "Atrasado" : "No prazo") : ""}
                    accent={behavior.daysSinceLast !== null && behavior.avgInterval && behavior.daysSinceLast > behavior.avgInterval * 1.3 ? "red" : "slate"}
                    series={[]}
                    testId="kpi-last-visit"
                />
                <div className="gold-panel p-4 flex flex-col justify-between">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">Status</div>
                    <span
                        className={`mt-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border ${statusStyle.border} ${statusStyle.bg} ${statusStyle.text} text-[11px] font-bold uppercase tracking-wider self-start`}
                    >
                        <i className={`fa-solid ${
                            statusStyle.id === "VIP" ? "fa-crown" : statusStyle.id === "Em Dia" ? "fa-check" : "fa-triangle-exclamation"
                        }`}></i>
                        {statusStyle.label}
                    </span>
                </div>
            </div>

            {/* Two columns: Consumo + Sugestões */}
            <div className="grid lg:grid-cols-2 gap-3">
                {/* Top services / consumption */}
                <section className="gold-panel p-5" data-testid="clients-consumption">
                    <header className="flex items-center justify-between pb-3 gold-border-bottom">
                        <h3 className="gold-text text-base uppercase">
                            <i className="fa-solid fa-chart-bar mr-2"></i>Análise de Consumo
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                            {behavior.topServices.length} serviço(s)
                        </span>
                    </header>
                    <div className="mt-4 space-y-2.5 max-h-[280px] overflow-y-auto pr-1">
                        {behavior.topServices.length === 0 && (
                            <p className="text-center text-amber-200/40 text-sm py-8">
                                <i className="fa-solid fa-folder-open text-2xl block mb-2"></i>
                                Sem histórico ainda
                            </p>
                        )}
                        {behavior.topServices.slice(0, 6).map((s, idx) => {
                            const pct = (s.count / topServiceMax) * 100;
                            const isTop = idx === 0;
                            return (
                                <div key={s.name} data-testid={`clients-service-row-${idx}`} className="space-y-1">
                                    <div className="flex items-baseline justify-between gap-2 text-xs">
                                        <span className={`truncate font-semibold ${isTop ? "text-amber-100" : "text-amber-200/80"}`}>
                                            {isTop && <i className="fa-solid fa-crown text-amber-400 text-[10px] mr-1.5"></i>}
                                            {s.name}
                                        </span>
                                        <span className="font-mono text-[11px] text-amber-300/80 shrink-0">
                                            {s.count}× · R$ {s.total.toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="h-2 bg-black/60 rounded-full overflow-hidden border border-amber-500/15">
                                        <div
                                            className="h-full rounded-full transition-all duration-700"
                                            style={{
                                                width: `${pct}%`,
                                                background: isTop
                                                    ? "linear-gradient(90deg, #d4af37, #fbbf24)"
                                                    : "linear-gradient(90deg, rgba(212,175,55,0.6), rgba(251,191,36,0.4))",
                                                boxShadow: isTop ? "0 0 12px rgba(212,175,55,0.4)" : "none",
                                            }}
                                        ></div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Marketing suggestions */}
                <section className="gold-panel p-5" data-testid="clients-suggestions">
                    <header className="flex items-center justify-between pb-3 gold-border-bottom">
                        <h3 className="gold-text text-base uppercase">
                            <i className="fa-solid fa-lightbulb mr-2"></i>Sugestões de Marketing
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                            Insights de decisão
                        </span>
                    </header>
                    <div className="mt-4 space-y-2.5 max-h-[280px] overflow-y-auto pr-1">
                        {suggestions.map((s, idx) => (
                            <SuggestionCard
                                key={idx}
                                suggestion={s}
                                testId={`clients-suggestion-${s.kind}`}
                                onAction={() => handleSuggestionAction(s)}
                            />
                        ))}
                    </div>
                </section>
            </div>

            {/* Histórico Financeiro (chart) */}
            <section className="gold-panel p-5" data-testid="clients-chart-panel">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">
                        <i className="fa-solid fa-chart-line mr-2"></i>Histórico Financeiro
                    </h3>
                    <span className="text-[11px] uppercase tracking-wider text-amber-200/70">
                        {chartData.buckets.reduce((s, b) => s + b.count, 0)} atendimentos no período
                    </span>
                </header>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
                    {CHART_PERIODS.map((p) => {
                        const active = chartPeriodId === p.id;
                        return (
                            <button
                                key={p.id}
                                data-testid={`clients-chart-period-${p.id}`}
                                onClick={() => setChartPeriodId(p.id)}
                                className={`gold-pill ${active ? "gold-pill-active" : ""} min-h-[44px] rounded-xl px-2 text-[10px] font-bold uppercase tracking-wider`}
                            >
                                {p.label}
                            </button>
                        );
                    })}
                </div>
                <div className="mt-6">
                    <SpendChart data={chartData} />
                </div>
            </section>

            {/* Period filter + transactions table */}
            <section className="gold-panel overflow-hidden" data-testid="clients-history-table">
                <header className="px-5 py-4 gold-border-bottom flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="gold-text text-base uppercase">
                        <i className="fa-solid fa-list-ul mr-2"></i>Histórico de atendimentos
                    </h3>
                    <div className="flex gap-1.5">
                        {Object.entries(PERIODS_SIMPLE).map(([id, p]) => {
                            const active = periodId === id;
                            return (
                                <button
                                    key={id}
                                    data-testid={`clients-period-${id}`}
                                    onClick={() => setPeriodId(id)}
                                    className={`min-h-[36px] px-3 rounded-lg border text-[10px] uppercase tracking-wider font-bold transition-all ${
                                        active
                                            ? "border-amber-400/60 bg-amber-500/15 text-amber-100"
                                            : "border-amber-500/15 bg-black/40 text-amber-200/60 hover:border-amber-500/40"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>
                </header>
                <div className="overflow-x-auto max-h-[360px]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-amber-300/70 bg-black/40 sticky top-0">
                                <th className="text-left py-3 px-4">Data</th>
                                <th className="text-left py-3 px-4">Serviço</th>
                                <th className="text-right py-3 px-4">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.length === 0 && (
                                <tr>
                                    <td colSpan={3} className="py-14 text-center text-amber-200/40">
                                        <i className="fa-solid fa-folder-open text-2xl mb-2"></i>
                                        <p>Sem atendimentos no período</p>
                                    </td>
                                </tr>
                            )}
                            {transactions.map((t) => (
                                <tr key={t.id} className="border-t border-amber-500/10 hover:bg-amber-500/5 transition-colors">
                                    <td className="py-3 px-4 font-mono text-xs text-amber-200/80">
                                        {new Date(t.date).toLocaleDateString("pt-BR")}
                                    </td>
                                    <td className="py-3 px-4 text-amber-100">{t.service}</td>
                                    <td className="py-3 px-4 text-right font-mono text-emerald-300 font-bold">
                                        R$ {(t.value || 0).toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

const SUGGESTION_PALETTE = {
    reactivate: { border: "border-amber-500/40", bg: "from-amber-500/12 to-transparent", icon: "text-amber-300", iconBg: "bg-amber-500/20 border-amber-400/40", title: "text-amber-100", cta: "text-amber-200" },
    upsell: { border: "border-sky-500/40", bg: "from-sky-500/12 to-transparent", icon: "text-sky-300", iconBg: "bg-sky-500/20 border-sky-400/40", title: "text-sky-100", cta: "text-sky-200" },
    vip: { border: "border-violet-500/40", bg: "from-violet-500/12 to-transparent", icon: "text-violet-300", iconBg: "bg-violet-500/20 border-violet-400/40", title: "text-violet-100", cta: "text-violet-200" },
    birthday: { border: "border-rose-500/40", bg: "from-rose-500/12 to-transparent", icon: "text-rose-300", iconBg: "bg-rose-500/20 border-rose-400/40", title: "text-rose-100", cta: "text-rose-200" },
    "all-good": { border: "border-emerald-500/30", bg: "from-emerald-500/8 to-transparent", icon: "text-emerald-300", iconBg: "bg-emerald-500/20 border-emerald-400/40", title: "text-emerald-100", cta: "text-emerald-200" },
};

const SuggestionCard = ({ suggestion, testId, onAction }) => {
    const pal = SUGGESTION_PALETTE[suggestion.kind] || SUGGESTION_PALETTE["all-good"];
    const hasAction = Boolean(suggestion.wizardSeed && onAction);
    const Wrapper = hasAction ? "button" : "div";
    return (
        <Wrapper
            data-testid={testId}
            onClick={hasAction ? onAction : undefined}
            className={`group relative w-full text-left rounded-xl border ${pal.border} bg-gradient-to-br ${pal.bg} bg-black/40 p-3 flex gap-3 transition-all ${
                hasAction ? "hover:-translate-y-0.5 hover:shadow-[0_18px_30px_-18px_rgba(212,175,55,0.45)] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50" : ""
            }`}
        >
            <div className={`h-10 w-10 shrink-0 rounded-xl flex items-center justify-center border ${pal.iconBg} ${pal.icon} ${hasAction ? "group-hover:scale-110 transition-transform duration-500" : ""}`}>
                <i className={`fa-solid ${suggestion.icon}`}></i>
            </div>
            <div className="min-w-0 flex-1">
                <h4 className={`text-sm font-bold ${pal.title} leading-tight`}>{suggestion.title}</h4>
                <p className="text-[11px] text-amber-200/70 mt-1 leading-relaxed">{suggestion.detail}</p>
                {suggestion.cta && (
                    <div className={`mt-1.5 text-[10px] uppercase tracking-wider font-bold ${pal.cta} flex items-center gap-1`}>
                        {suggestion.cta}
                        <i className={`fa-solid fa-arrow-right text-[9px] ${hasAction ? "group-hover:translate-x-1 transition-transform" : ""}`}></i>
                    </div>
                )}
            </div>
        </Wrapper>
    );
};

// ─── Cadastro do Cliente (chart shared with FinancialView) ────────────────
const CHART_PERIODS = [
    { id: "weekly", label: "Semanal", days: 7, buckets: 7, unit: "day" },
    { id: "biweekly", label: "Quinzenal", days: 15, buckets: 15, unit: "day" },
    { id: "monthly", label: "Mensal", days: 30, buckets: 4, unit: "week" },
    { id: "quarterly", label: "Trimestral", days: 90, buckets: 12, unit: "week" },
    { id: "semester", label: "Semestral", days: 180, buckets: 6, unit: "month" },
    { id: "annual", label: "Anual", days: 365, buckets: 12, unit: "month" },
];

const ClientRegistryView = ({ client, isNew, onDoneCreate, onCancelCreate }) => {
    const { upsertClient, deleteClient } = useSalon();
    const [draft, setDraft] = useState(isNew ? { ...client } : null);
    const [prefInput, setPrefInput] = useState("");
    const [saving, setSaving] = useState(false);

    const editing = draft || client;
    const setField = (k, v) => setDraft({ ...editing, [k]: v });

    const handleSave = async () => {
        if (saving) return;
        if (!editing.name.trim()) {
            toast.error("Informe o nome do cliente.");
            return;
        }
        if (isNew) {
            setSaving(true);
            try {
                const payload = { ...editing };
                delete payload.id;
                // upsertClient devolve uma Promise que sempre resolve (o request
                // de mirror ou um resolve imediato quando offline).
                await upsertClient(payload);
                toast.success("Cliente cadastrado");
                onDoneCreate?.();
            } finally {
                setSaving(false);
            }
            return;
        }
        if (!draft) {
            toast.info("Nenhuma alteração para salvar.");
            return;
        }
        setSaving(true);
        try {
            await upsertClient(editing);
            toast.success("Cadastro atualizado");
            setDraft(null);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        if (!client?.id) return;
        if (!window.confirm(`Excluir cliente ${client.name}?`)) return;
        deleteClient(client.id);
        toast.success("Cliente removido");
    };

    const handleAddPref = () => {
        const v = prefInput.trim();
        if (!v) return;
        setField("preferences", [...(editing.preferences || []), v]);
        setPrefInput("");
    };

    const handleRemovePref = (idx) => {
        setField("preferences", (editing.preferences || []).filter((_, i) => i !== idx));
    };

    return (
        <div data-testid="clients-registry-view" className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
            {/* Main: Ficha Cadastral */}
            <div className="gold-panel p-5 lg:p-7" data-testid="clients-registry-form">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">
                        <i className="fa-solid fa-id-card mr-2"></i>
                        {isNew ? "Novo Cliente" : "Ficha Cadastral"}
                    </h3>
                    <span className="text-[10px] uppercase tracking-wider text-amber-200/50">
                        Dados demográficos
                    </span>
                </header>
                <div className="space-y-4 mt-5">
                    <GoldField label="Nome Completo" testId="clients-field-name">
                        <input
                            value={editing.name}
                            onChange={(e) => setField("name", e.target.value)}
                            placeholder="Nome completo do cliente"
                            className={goldInputCls}
                        />
                    </GoldField>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <GoldField label="CPF" testId="clients-field-cpf">
                            <input
                                value={editing.document}
                                onChange={(e) => setField("document", maskCPF(e.target.value))}
                                placeholder="000.000.000-00"
                                className={`${goldInputCls} font-mono`}
                            />
                        </GoldField>
                        <GoldField label="Telefone" testId="clients-field-phone">
                            <input
                                value={editing.phone}
                                onChange={(e) => setField("phone", maskPhone(e.target.value))}
                                placeholder="(00) 00000-0000"
                                className={`${goldInputCls} font-mono`}
                            />
                        </GoldField>
                        <GoldField label="Sexo" testId="clients-field-gender">
                            <select
                                value={editing.gender || ""}
                                onChange={(e) => setField("gender", e.target.value)}
                                className={goldInputCls}
                            >
                                <option value="">Selecionar...</option>
                                <option value="Feminino">Feminino</option>
                                <option value="Masculino">Masculino</option>
                                <option value="Não-binário">Não-binário</option>
                                <option value="Prefere não informar">Prefere não informar</option>
                            </select>
                        </GoldField>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <GoldField label="E-mail" testId="clients-field-email">
                            <input
                                type="email"
                                value={editing.email}
                                onChange={(e) => setField("email", e.target.value)}
                                placeholder="cliente@email.com"
                                className={goldInputCls}
                            />
                        </GoldField>
                        <GoldField label="Data de Nascimento" testId="clients-field-birth">
                            <input
                                type="date"
                                value={editing.birthDate}
                                onChange={(e) => setField("birthDate", e.target.value)}
                                className={`${goldInputCls} font-mono`}
                            />
                        </GoldField>
                    </div>

                    <GoldField label="Endereço Completo" testId="clients-field-address">
                        <input
                            value={editing.address}
                            onChange={(e) => setField("address", e.target.value)}
                            placeholder="Rua, número, bairro - Cidade/UF"
                            className={goldInputCls}
                        />
                    </GoldField>

                    {/* Preferências */}
                    <GoldField label="Preferências" testId="clients-field-prefs">
                        <div className="space-y-2">
                            <div className="flex gap-2">
                                <input
                                    value={prefInput}
                                    onChange={(e) => setPrefInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddPref())}
                                    placeholder="Ex: Corte degradê, café sem açúcar..."
                                    className={`${goldInputCls} flex-1`}
                                />
                                <button
                                    data-testid="clients-add-pref"
                                    type="button"
                                    onClick={handleAddPref}
                                    className="min-h-[44px] px-3 rounded-xl border border-amber-500/30 hover:border-amber-500/60 text-amber-200 transition-all"
                                >
                                    <i className="fa-solid fa-plus"></i>
                                </button>
                            </div>
                            {(editing.preferences || []).length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {(editing.preferences || []).map((p, i) => (
                                        <span
                                            key={i}
                                            data-testid={`clients-pref-${i}`}
                                            className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-lg border border-amber-500/25 bg-amber-500/8 text-amber-200 text-[11px]"
                                        >
                                            {p}
                                            <button
                                                onClick={() => handleRemovePref(i)}
                                                className="h-5 w-5 rounded text-amber-300/60 hover:text-red-400 hover:bg-red-500/15 transition-all"
                                            >
                                                <i className="fa-solid fa-xmark text-[9px]"></i>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </GoldField>

                    <GoldField label="Alergias / Contraindicações" testId="clients-field-allergies">
                        <input
                            value={editing.allergies}
                            onChange={(e) => setField("allergies", e.target.value)}
                            placeholder="Ex: alergia a sulfato, dermatite..."
                            className={goldInputCls}
                        />
                    </GoldField>

                    <GoldField label="Observações Internas" testId="clients-field-notes">
                        <textarea
                            value={editing.notes}
                            onChange={(e) => setField("notes", e.target.value)}
                            rows={3}
                            placeholder="Observações internas sobre o cliente..."
                            className={`${goldInputCls} resize-none`}
                        />
                    </GoldField>

                    <div className="flex items-center justify-between gap-2 pt-2">
                        {isNew ? (
                            <button
                                data-testid="clients-cancel-create"
                                onClick={onCancelCreate}
                                className="min-h-[44px] px-4 rounded-xl border border-amber-500/20 hover:border-amber-500/45 text-amber-200/80 text-[11px] uppercase tracking-wider font-bold transition-all"
                            >
                                Cancelar
                            </button>
                        ) : (
                            <button
                                data-testid="clients-delete-btn"
                                onClick={handleDelete}
                                className="min-h-[44px] h-11 w-11 rounded-xl border border-red-500/25 hover:border-red-500/60 text-red-400 transition-all"
                                title="Excluir cliente"
                            >
                                <i className="fa-solid fa-trash text-xs"></i>
                            </button>
                        )}
                        <button
                            data-testid="clients-save-btn"
                            onClick={handleSave}
                            disabled={saving}
                            className="gold-pill gold-pill-active min-h-[48px] px-6 rounded-xl text-xs font-bold uppercase tracking-[0.15em] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-floppy-disk"} mr-2`}></i>
                            {saving ? "Salvando…" : isNew ? "Cadastrar" : "Salvar"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Side: Status & Vínculo */}
            <aside className="space-y-4">
                <section className="gold-panel p-5" data-testid="clients-status-panel">
                    <h3 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom">
                        <i className="fa-solid fa-circle-check mr-2"></i>Status do Cliente
                    </h3>
                    <div className="mt-3 grid grid-cols-1 gap-2">
                        {STATUS_OPTIONS.map((s) => {
                            const active = editing.paymentStatus === s.id;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    data-testid={`clients-status-${s.id.replace(" ", "-")}`}
                                    onClick={() => setField("paymentStatus", s.id)}
                                    className={`min-h-[44px] rounded-xl border px-3 text-left text-[11px] uppercase tracking-wider font-bold transition-all flex items-center gap-2 ${
                                        active
                                            ? `${s.border} ${s.bg} ${s.text}`
                                            : "border-amber-500/15 bg-black/40 text-amber-200/50 hover:border-amber-500/40"
                                    }`}
                                >
                                    <i className={`fa-solid ${
                                        s.id === "VIP" ? "fa-crown" : s.id === "Em Dia" ? "fa-check" : "fa-triangle-exclamation"
                                    }`}></i>
                                    {s.label}
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* Quick info card */}
                {!isNew && (
                    <section className="gold-panel p-5">
                        <h3 className="gold-text text-sm uppercase tracking-wider pb-3 gold-border-bottom">
                            <i className="fa-solid fa-fingerprint mr-2"></i>Identidade
                        </h3>
                        <dl className="mt-3 space-y-2 text-xs">
                            <InfoRow label="ID" value={client.id} mono />
                            <InfoRow label="Cadastrado em" value={formatCreatedAt(client.createdAt)} mono />
                            <InfoRow label="Atendimentos" value={(client.transactions || []).length} mono />
                            <InfoRow
                                label="Total na vida"
                                value={`R$ ${((client.transactions || []).reduce((s, t) => s + (t.value || 0), 0)).toFixed(2)}`}
                                highlight
                            />
                        </dl>
                    </section>
                )}
            </aside>
        </div>
    );
};

const formatCreatedAt = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const InfoRow = ({ label, value, mono, highlight }) => (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-amber-500/8 last:border-b-0">
        <span className="text-[10px] uppercase tracking-wider text-amber-300/70 font-bold">{label}</span>
        <span className={`truncate ${mono ? "font-mono" : ""} ${highlight ? "text-emerald-300 font-bold" : "text-amber-100"}`}>
            {value}
        </span>
    </div>
);


const SpendChart = ({ data }) => {
    const { buckets, maxValue } = data;
    return (
        <div data-testid="clients-spend-chart" className="relative h-72 rounded-xl border border-amber-500/15 bg-black/40 p-4 overflow-hidden">
            {[1, 0.75, 0.5, 0.25, 0].map((p) => (
                <div
                    key={p}
                    className="absolute left-4 right-4 border-t border-amber-500/8 flex items-center"
                    style={{ top: `calc(${(1 - p) * 100}% + 16px - 1px)` }}
                >
                    <span className="text-[9px] font-mono text-amber-300/50 -mt-2 -ml-1 px-1 bg-black/60">
                        R$ {Math.round(maxValue * p)}
                    </span>
                </div>
            ))}

            <div className="absolute inset-4 flex items-end justify-between gap-1">
                {buckets.map((b, i) => {
                    const h = (b.total / maxValue) * 100;
                    return (
                        <div
                            key={i}
                            data-testid={`clients-bar-${i}`}
                            className="flex-1 flex flex-col items-center justify-end gap-1 h-full group"
                            title={`${b.label} · R$ ${b.total.toFixed(2)} (${b.count} atendimento(s))`}
                        >
                            <div className="relative w-full max-w-[36px] flex-1 flex items-end">
                                <div
                                    className="absolute bottom-0 left-1/4 right-1/4 rounded-t bg-amber-400 transition-all group-hover:brightness-125"
                                    style={{
                                        height: `${h}%`,
                                        animation: `chartGrow 0.6s ease-out ${i * 40}ms both`,
                                        boxShadow: "0 -2px 12px rgba(212,175,55,0.4)",
                                    }}
                                ></div>
                            </div>
                            <span className="text-[9px] font-mono text-amber-200/60 truncate max-w-full">
                                {b.label}
                            </span>
                        </div>
                    );
                })}
            </div>
            <style>{`@keyframes chartGrow { from { height: 0%; opacity: 0; } to { opacity: 1; } }`}</style>
        </div>
    );
};

// ─── VIEW: Marketing do Cliente ──────────────────────────────────────────
const ClientMarketingView = ({ client }) => {
    const { campaigns, templates, messageLog, setActiveModule, setPendingWizardSeed } = useSalon();

    // All messages this client received, sorted DESC
    const clientMessages = useMemo(() => {
        return [...(messageLog || [])]
            .filter((m) => m.clientId === client.id)
            .map((m) => {
                const cmp = campaigns.find((c) => c.id === m.campaignId);
                const tpl = templates.find((t) => t.id === cmp?.templateId);
                return { ...m, campaign: cmp, template: tpl };
            })
            .sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0));
    }, [messageLog, campaigns, templates, client]);

    // KPIs
    const kpis = useMemo(() => {
        const total = clientMessages.length;
        const converted = clientMessages.filter((m) => m.convertedAt).length;
        const conversionRate = total > 0 ? (converted / total) * 100 : 0;
        const ratings = clientMessages.filter((m) => m.feedback?.rating).map((m) => m.feedback.rating);
        const avgRating = ratings.length > 0 ? ratings.reduce((s, r) => s + r, 0) / ratings.length : null;
        const lastSent = clientMessages[0]?.sentAt || null;
        const daysSinceLast = lastSent ? Math.floor((Date.now() - lastSent) / 86400000) : null;
        // Group by campaign
        const byCampaign = {};
        clientMessages.forEach((m) => {
            const k = m.campaignId;
            if (!byCampaign[k]) byCampaign[k] = { campaign: m.campaign, sent: 0, converted: 0 };
            byCampaign[k].sent++;
            if (m.convertedAt) byCampaign[k].converted++;
        });
        return { total, converted, conversionRate, avgRating, daysSinceLast, byCampaign: Object.values(byCampaign) };
    }, [clientMessages]);

    // Active campaigns this client currently qualifies for
    const txns = client.transactions || [];
    const lastVisit = txns.length > 0 ? Math.max(...txns.map((t) => t.date)) : null;
    const daysSinceVisit = lastVisit ? Math.floor((Date.now() - lastVisit) / 86400000) : null;
    const eligibleCampaigns = useMemo(() => {
        return campaigns
            .filter((c) => c.active)
            .map((c) => {
                const trigger = c.trigger || {};
                let eligible = false;
                let reason = "";
                if (trigger.type === "inactive") {
                    const daysAway = trigger.config?.daysAway || 30;
                    if (daysSinceVisit !== null && daysSinceVisit >= daysAway) {
                        eligible = true;
                        reason = `Cliente parado há ${daysSinceVisit}d (gatilho: ${daysAway}d)`;
                    }
                } else if (trigger.type === "birthday-month") {
                    if (client.birthDate) {
                        const m = parseInt(client.birthDate.split("-")[1], 10);
                        const today = new Date();
                        if (m === today.getMonth() + 1) {
                            eligible = true;
                            reason = "Aniversariante deste mês";
                        }
                    }
                } else if (trigger.type === "upsell") {
                    const base = (trigger.config?.baseService || "").toLowerCase();
                    if (txns.some((t) => (t.service || "").toLowerCase().includes(base))) {
                        eligible = true;
                        reason = `Já consumiu "${trigger.config?.baseService}"`;
                    }
                } else if (trigger.type === "post-service") {
                    if (lastVisit && Date.now() - lastVisit < 7 * 86400000) {
                        eligible = true;
                        reason = "Atendimento nos últimos 7 dias";
                    }
                }
                return { ...c, _eligible: eligible, _reason: reason };
            });
    }, [campaigns, daysSinceVisit, client, txns, lastVisit]);

    const activeForClient = eligibleCampaigns.filter((c) => c._eligible);
    const dormant = eligibleCampaigns.filter((c) => !c._eligible);

    const handleSendNow = (campaign) => {
        setPendingWizardSeed({
            clientId: client.id,
            clientName: client.name,
            audienceId: campaign.trigger?.type || "manual",
            templateId: campaign.templateId,
        });
        setActiveModule("marketing");
        toast.success("Wizard pré-preenchido", {
            description: `Marketing → ${campaign.name}`,
        });
    };

    return (
        <div data-testid="clients-marketing-view" className="space-y-4">
            {/* Hero KPIs with mini sparklines (Power-BI style) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="clients-mkt-kpis">
                <KpiWithSpark
                    icon="fa-paper-plane"
                    label="Mensagens recebidas"
                    value={kpis.total}
                    accent="amber"
                    series={buildMonthlySeries(clientMessages, (m) => m.sentAt)}
                    testId="mkt-kpi-sent"
                />
                <KpiWithSpark
                    icon="fa-bullseye"
                    label="Taxa de conversão"
                    value={`${kpis.conversionRate.toFixed(0)}%`}
                    sub={`${kpis.converted} de ${kpis.total} aderiram`}
                    accent="emerald"
                    series={buildMonthlySeries(clientMessages.filter((m) => m.convertedAt), (m) => m.convertedAt)}
                    testId="mkt-kpi-conversion"
                />
                <KpiWithSpark
                    icon="fa-star"
                    label="Avaliação média"
                    value={kpis.avgRating !== null ? kpis.avgRating.toFixed(1) : "—"}
                    sub={kpis.avgRating !== null ? `${kpis.avgRating >= 4 ? "Promotor" : kpis.avgRating >= 3 ? "Neutro" : "Detrator"}` : "Sem feedback"}
                    accent={kpis.avgRating === null ? "slate" : kpis.avgRating >= 4 ? "emerald" : kpis.avgRating >= 3 ? "amber" : "red"}
                    ratings={clientMessages.map((m) => m.feedback?.rating).filter(Boolean)}
                    testId="mkt-kpi-rating"
                />
                <KpiWithSpark
                    icon="fa-bolt"
                    label="Campanhas ativas"
                    value={activeForClient.length}
                    sub={activeForClient.length > 0 ? "elegível para envio" : "nada urgente"}
                    accent={activeForClient.length > 0 ? "amber" : "slate"}
                    series={[]}
                    testId="mkt-kpi-active"
                />
            </div>

            {/* Active campaigns for this client */}
            <section className="gold-panel p-5" data-testid="clients-mkt-active-campaigns">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">
                        <i className="fa-solid fa-fire mr-2"></i>Campanhas ATIVAS para este cliente
                    </h3>
                    <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                        {activeForClient.length} elegíveis · {dormant.length} inativas
                    </span>
                </header>
                {activeForClient.length === 0 ? (
                    <div className="mt-5 text-center py-8 text-amber-200/40 text-sm">
                        <i className="fa-solid fa-mug-saucer text-2xl block mb-2"></i>
                        Nenhuma campanha disparando para este cliente agora.
                    </div>
                ) : (
                    <div className="mt-4 space-y-2.5">
                        {activeForClient.map((c) => (
                            <div
                                key={c.id}
                                data-testid={`mkt-active-${c.id}`}
                                className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-3"
                            >
                                <div className="h-10 w-10 rounded-lg bg-amber-500/15 border border-amber-500/40 flex items-center justify-center text-amber-300 flex-shrink-0">
                                    <i className="fa-solid fa-bullhorn"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-amber-100 truncate">{c.name}</div>
                                    <div className="text-xs text-emerald-300 mt-0.5">
                                        <i className="fa-solid fa-circle-check mr-1"></i>{c._reason}
                                    </div>
                                </div>
                                <button
                                    data-testid={`mkt-send-${c.id}`}
                                    onClick={() => handleSendNow(c)}
                                    className="min-h-[40px] px-3 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 hover:border-emerald-400 text-emerald-200 text-[11px] uppercase tracking-wider font-bold transition-all flex items-center gap-2"
                                >
                                    <i className="fa-brands fa-whatsapp"></i>
                                    Enviar agora
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* History: oferecidas + aderência */}
            <div className="grid lg:grid-cols-2 gap-3">
                <section className="gold-panel p-5" data-testid="clients-mkt-history">
                    <header className="flex items-center justify-between pb-3 gold-border-bottom">
                        <h3 className="gold-text text-base uppercase">
                            <i className="fa-solid fa-clock-rotate-left mr-2"></i>Histórico (ofertado vs aderido)
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider text-amber-200/60">{clientMessages.length} envios</span>
                    </header>
                    {clientMessages.length === 0 ? (
                        <div className="mt-6 text-center py-8 text-amber-200/40 text-sm">
                            <i className="fa-solid fa-inbox text-2xl block mb-2"></i>
                            Nenhuma campanha enviada ainda.
                        </div>
                    ) : (
                        <div className="mt-4 space-y-2 max-h-[360px] overflow-y-auto pr-1">
                            {clientMessages.map((m) => {
                                const aderiu = !!m.convertedAt;
                                const sentDate = new Date(m.sentAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
                                const convDate = m.convertedAt
                                    ? new Date(m.convertedAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
                                    : null;
                                return (
                                    <div
                                        key={m.id}
                                        data-testid={`mkt-msg-${m.id}`}
                                        className={`rounded-xl border p-3 ${
                                            aderiu
                                                ? "border-emerald-500/30 bg-emerald-500/5"
                                                : "border-amber-500/15 bg-black/40"
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                                aderiu ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40" : "bg-slate-800 text-slate-300 border border-white/5"
                                            }`}>
                                                <i className={`fa-solid ${aderiu ? "fa-check-double" : "fa-paper-plane"}`}></i>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                                    <div className="text-sm font-bold text-white truncate">
                                                        {m.campaign?.name || m.campaignId}
                                                    </div>
                                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md ${
                                                        aderiu
                                                            ? "bg-emerald-500/20 text-emerald-300"
                                                            : "bg-amber-500/15 text-amber-300/80"
                                                    }`}>
                                                        {aderiu ? "Aderiu" : "Apenas ofertado"}
                                                    </span>
                                                </div>
                                                {m.template?.body && (
                                                    <div className="text-[11px] text-slate-400 italic mt-1 line-clamp-2">
                                                        &ldquo;{m.template.body.replace(/\{\{[^}]+\}\}/g, "…")}&rdquo;
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-3 mt-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                                                    <span><i className="fa-solid fa-paper-plane mr-1"></i>{sentDate}</span>
                                                    {convDate && (
                                                        <span className="text-emerald-300">
                                                            <i className="fa-solid fa-check mr-1"></i>Aderiu em {convDate}
                                                        </span>
                                                    )}
                                                    {m.feedback?.rating && (
                                                        <span className={`${m.feedback.rating >= 4 ? "text-emerald-300" : m.feedback.rating >= 3 ? "text-amber-300" : "text-red-400"}`}>
                                                            <i className="fa-solid fa-star mr-1"></i>{m.feedback.rating}/5
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Per-campaign breakdown bars */}
                <section className="gold-panel p-5" data-testid="clients-mkt-breakdown">
                    <header className="flex items-center justify-between pb-3 gold-border-bottom">
                        <h3 className="gold-text text-base uppercase">
                            <i className="fa-solid fa-chart-pie mr-2"></i>Aderência por campanha
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                            {kpis.byCampaign.length} campanha(s)
                        </span>
                    </header>
                    {kpis.byCampaign.length === 0 ? (
                        <div className="mt-6 text-center py-8 text-amber-200/40 text-sm">
                            <i className="fa-solid fa-chart-line text-2xl block mb-2"></i>
                            Sem dados de aderência ainda.
                        </div>
                    ) : (
                        <div className="mt-4 space-y-3">
                            {kpis.byCampaign.map((row) => {
                                const rate = row.sent > 0 ? (row.converted / row.sent) * 100 : 0;
                                return (
                                    <div key={row.campaign?.id} className="space-y-1">
                                        <div className="flex items-baseline justify-between text-xs">
                                            <span className="font-semibold text-amber-100 truncate">{row.campaign?.name || "—"}</span>
                                            <span className="font-mono text-[11px] text-amber-300/80 shrink-0">
                                                {row.converted}/{row.sent} · {rate.toFixed(0)}%
                                            </span>
                                        </div>
                                        <div className="h-2.5 bg-black/60 rounded-full overflow-hidden border border-amber-500/15 relative">
                                            <div
                                                className="absolute inset-y-0 left-0 bg-amber-500/25"
                                                style={{ width: "100%" }}
                                            ></div>
                                            <div
                                                className="absolute inset-y-0 left-0 transition-all duration-700"
                                                style={{
                                                    width: `${rate}%`,
                                                    background: "linear-gradient(90deg, #10b981, #34d399)",
                                                    boxShadow: "0 0 12px rgba(16,185,129,0.4)",
                                                }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Inactive campaigns (informational) */}
                    {dormant.length > 0 && (
                        <details className="mt-6 group">
                            <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-amber-200/60 hover:text-amber-200 transition-colors">
                                <i className="fa-solid fa-chevron-right mr-1.5 group-open:rotate-90 transition-transform"></i>
                                {dormant.length} campanhas existentes mas não elegíveis para este cliente
                            </summary>
                            <div className="mt-2 space-y-1 pl-4">
                                {dormant.map((c) => (
                                    <div key={c.id} className="text-[10px] text-slate-500 flex items-center gap-2">
                                        <i className="fa-solid fa-circle text-[5px]"></i>
                                        <span className="truncate">{c.name}</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}
                </section>
            </div>
        </div>
    );
};

// Helper: build monthly sparkline series for the last 6 months
const buildMonthlySeries = (items, getDate) => {
    const buckets = Array(6).fill(0);
    const now = new Date();
    items.forEach((it) => {
        const ts = getDate(it);
        if (!ts) return;
        const d = new Date(ts);
        const monthsAgo = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
        if (monthsAgo >= 0 && monthsAgo < 6) buckets[5 - monthsAgo]++;
    });
    return buckets;
};

const KpiWithSpark = ({ icon, label, value, sub, accent, series, ratings, testId }) => {
    const accents = {
        amber: { ring: "border-amber-500/25", iconBg: "bg-amber-500/15 text-amber-300", val: "text-amber-100", barColor: "rgba(212,175,55,0.85)" },
        emerald: { ring: "border-emerald-500/25", iconBg: "bg-emerald-500/15 text-emerald-300", val: "text-emerald-100", barColor: "rgba(16,185,129,0.85)" },
        red: { ring: "border-red-500/25", iconBg: "bg-red-500/15 text-red-300", val: "text-red-100", barColor: "rgba(239,68,68,0.85)" },
        slate: { ring: "border-white/8", iconBg: "bg-slate-700/40 text-slate-300", val: "text-slate-100", barColor: "rgba(148,163,184,0.6)" },
    };
    const a = accents[accent] || accents.amber;
    return (
        <div data-testid={testId} className={`rounded-xl border ${a.ring} bg-black/55 p-3 flex items-center gap-3 hover:bg-black/65 transition-colors`}>
            <div className={`h-11 w-11 rounded-lg ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
                <i className={`fa-solid ${icon}`}></i>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 leading-tight">{label}</div>
                <div className={`salon-heading text-2xl ${a.val} leading-tight mt-0.5`}>{value}</div>
                {sub && <div className="text-[10px] text-slate-400 mt-0.5 truncate">{sub}</div>}
            </div>
            {/* Mini chart on the right */}
            {ratings && ratings.length > 0 ? (
                <RatingMini ratings={ratings} />
            ) : (
                <Sparkline series={series || []} color={a.barColor} />
            )}
        </div>
    );
};

const Sparkline = ({ series, color = "rgba(212,175,55,0.85)" }) => {
    if (!series || series.length === 0) {
        return (
            <div className="w-16 h-10 flex items-end justify-between gap-0.5 opacity-30">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="flex-1 bg-slate-700 rounded-sm" style={{ height: "20%" }}></div>
                ))}
            </div>
        );
    }
    const max = Math.max(1, ...series);
    return (
        <div className="w-16 h-10 flex items-end justify-between gap-0.5">
            {series.map((v, i) => {
                const pct = (v / max) * 100;
                return (
                    <div
                        key={i}
                        className="flex-1 rounded-sm transition-all"
                        style={{
                            height: `${Math.max(6, pct)}%`,
                            background: color,
                            opacity: i === series.length - 1 ? 1 : 0.55 + (i / (series.length - 1)) * 0.4,
                        }}
                        title={`${v}`}
                    />
                );
            })}
        </div>
    );
};

const RatingMini = ({ ratings }) => {
    // Distribution 1..5
    const dist = [0, 0, 0, 0, 0];
    ratings.forEach((r) => {
        const idx = Math.min(5, Math.max(1, Math.round(r))) - 1;
        dist[idx]++;
    });
    const max = Math.max(1, ...dist);
    return (
        <div className="w-16 h-10 flex items-end justify-between gap-0.5">
            {dist.map((v, i) => {
                const pct = (v / max) * 100;
                const color = i >= 3 ? "rgba(16,185,129,0.85)" : i === 2 ? "rgba(212,175,55,0.85)" : "rgba(239,68,68,0.85)";
                return (
                    <div
                        key={i}
                        className="flex-1 rounded-sm"
                        style={{
                            height: `${Math.max(6, pct)}%`,
                            background: v > 0 ? color : "rgba(148,163,184,0.2)",
                        }}
                        title={`${i + 1}★: ${v}`}
                    />
                );
            })}
        </div>
    );
};



// ─── VIEW: Próximos Agendamentos do Cliente ──────────────────────────────
const ClientScheduleView = ({ client }) => {
    const { appointments, professionals } = useSalon();

    const upcoming = useMemo(() => {
        const now = Date.now();
        const proMap = Object.fromEntries(professionals.map((p) => [p.id, p]));
        return appointments
            .filter((a) => {
                if (!a.date || a.date < now) return false;
                if (a.status === "cancelado") return false;
                // Match by clientId (preferred) or clientName as fallback
                if (a.clientId) return a.clientId === client.id;
                return (a.clientName || "").toLowerCase().trim() === (client.name || "").toLowerCase().trim();
            })
            .map((a) => ({
                ...a,
                professional: a.professionalName || proMap[a.professionalId]?.name || "—",
                proAvatar: proMap[a.professionalId]?.avatar,
                service: a.serviceName,
            }))
            .sort((a, b) => a.date - b.date);
    }, [appointments, professionals, client]);

    // Agrupa por dia
    const grouped = useMemo(() => {
        const map = {};
        upcoming.forEach((a) => {
            const d = new Date(a.date);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            if (!map[key]) map[key] = { date: d, items: [] };
            map[key].items.push(a);
        });
        return Object.values(map);
    }, [upcoming]);

    return (
        <div data-testid="clients-schedule-view" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <GoldKpi label="Total agendados" value={upcoming.length} icon="fa-calendar-check" />
                <GoldKpi
                    label="Próximo"
                    value={
                        upcoming[0]
                            ? new Date(upcoming[0].date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
                            : "—"
                    }
                    icon="fa-clock"
                />
                <GoldKpi label="Dias com agenda" value={grouped.length} icon="fa-calendar-days" />
                <GoldKpi
                    label="Último horário marcado"
                    value={
                        upcoming[upcoming.length - 1]
                            ? new Date(upcoming[upcoming.length - 1].date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
                            : "—"
                    }
                    icon="fa-flag-checkered"
                    highlight
                />
            </div>

            <div className="gold-panel p-5">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">Lista de próximos agendamentos</h3>
                    <span className="text-[10px] uppercase tracking-wider text-amber-200/60">
                        {upcoming.length} compromisso(s)
                    </span>
                </header>

                {grouped.length === 0 && (
                    <div className="py-14 text-center text-amber-200/40">
                        <i className="fa-solid fa-calendar-xmark text-3xl mb-3"></i>
                        <p className="text-sm">Este cliente não possui agendamentos futuros.</p>
                        <p className="text-[11px] mt-1 italic">
                            Para agendar, vá em <span className="text-amber-300">Colaboradores → Agenda Semanal</span>.
                        </p>
                    </div>
                )}

                <div className="space-y-4 mt-4 max-h-[520px] overflow-y-auto pr-2">
                    {grouped.map((g, gi) => (
                        <div key={gi} data-testid={`clients-schedule-group-${gi}`}>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-amber-500/10 border border-amber-500/25">
                                    <span className="salon-heading text-amber-300">
                                        {String(g.date.getDate()).padStart(2, "0")}
                                    </span>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/70">
                                        {g.date.toLocaleDateString("pt-BR", { weekday: "long" })}
                                    </div>
                                    <div className="text-xs text-amber-200/60 font-mono">
                                        {g.date.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {g.items.map((a) => (
                                    <article
                                        key={a.id}
                                        data-testid={`clients-appt-${a.id}`}
                                        className="rounded-xl border border-amber-500/20 bg-black/40 px-4 py-3 hover:border-amber-500/40 transition-all flex items-center gap-3"
                                    >
                                        <div className="text-center min-w-[60px]">
                                            <div className="font-mono text-lg text-amber-200 font-bold">{a.time}</div>
                                            <div className="text-[9px] uppercase tracking-wider text-amber-300/60">
                                                {a.status === "agendado" ? "Marcado" : a.status === "em-andamento" ? "Andamento" : "Concluído"}
                                            </div>
                                        </div>
                                        <div className="h-10 border-l border-amber-500/15"></div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-amber-100 font-semibold truncate">{a.service}</div>
                                            <div className="text-[11px] text-amber-200/60 flex items-center gap-1.5 mt-0.5">
                                                <i className="fa-solid fa-user-tie text-[9px]"></i>
                                                <span className="truncate">com {a.professional}</span>
                                            </div>
                                        </div>
                                        <img
                                            src={a.proAvatar}
                                            alt=""
                                            className="h-9 w-9 rounded-lg object-cover ring-1 ring-amber-500/30"
                                        />
                                    </article>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── Helpers compartilhados ──────────────────────────────────────────────
const goldInputCls =
    "w-full bg-black/60 border border-amber-500/22 rounded-xl px-3.5 py-2.5 text-amber-100 text-sm outline-none focus:border-amber-400/55 transition-colors min-h-[44px] placeholder:text-amber-200/30";

const GoldField = ({ label, testId, children }) => (
    <label className="block" data-testid={testId}>
        <span className="text-[11px] tracking-[0.15em] text-amber-300/80 font-semibold">{label}</span>
        <div className="mt-1.5">{children}</div>
    </label>
);

const GoldKpi = ({ label, value, icon, highlight }) => (
    <div
        className={`gold-panel p-4 ${
            highlight ? "ring-1 ring-emerald-400/40 shadow-[0_0_30px_-15px_rgba(16,185,129,0.5)]" : ""
        }`}
    >
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">{label}</div>
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-500/15 text-amber-300">
                <i className={`fa-solid ${icon} text-sm`}></i>
            </div>
        </div>
        <div
            className="salon-heading text-2xl mt-2"
            style={{ color: highlight ? "#34d399" : "#fde7a8" }}
        >
            {value}
        </div>
    </div>
);

const formatBucketLabel = (date, unit) => {
    if (unit === "day") {
        return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    }
    if (unit === "week") {
        return `S${Math.ceil(date.getDate() / 7)}/${String(date.getMonth() + 1).padStart(2, "0")}`;
    }
    return date.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
};

export default Clients;
